import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Search,
  User,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Play,
  RotateCcw,
  Clock,
  Scissors,
  BookOpen,
  Film,
  Wallet,
  Trash2,
  MessageCircle,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import {
  useAdminUserQuery,
  useAdminProjectsQuery,
  useAdminClipsQuery,
  useAdminRestoreExpiredItemsMutation,
  useAdminRestoreExpiredProjectMutation,
  useAdminRestoreExpiredClipMutation,
  useAdminRetryFailedProjectMutation,
  useAdminRetryFailedClipMutation,
  AdminClipsQuery,
  useAdminUserCpmWalletQuery,
  useAdminDeleteCpmPayoutMutation,
} from "@/generated/graphql";
import { formatDateTime, formatDuration, formatCents } from "@/lib/format";
import { RegisterPayoutDialog } from "@/components/admin/RegisterPayoutDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatHoursMinutes } from "@/lib/video-metadata";
import { useToast } from "@/hooks/use-toast";
import {
  UserReliabilityAccounts,
  UserReliabilitySummary,
} from "@/components/admin/UserReliabilityAccounts";

type AdminClip = AdminClipsQuery["adminClips"]["data"][number];

const AdminUserDetail = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { id } = useParams<{ id: string }>();

  // Projects state
  const [projectSearch, setProjectSearch] = useState("");
  const [projectPage, setProjectPage] = useState(1);
  const projectPageSize = 10;

  // Clips state
  const [clipSearch, setClipSearch] = useState("");
  const [clipPage, setClipPage] = useState(1);
  const clipPageSize = 10;
  const [videoPreviewClip, setVideoPreviewClip] = useState<AdminClip | null>(
    null
  );
  const [videoPreviewType, setVideoPreviewType] = useState<'clipped' | 'rendered'>('rendered');

  const { data: userData, loading: userLoading } = useAdminUserQuery({
    variables: { id: id! },
    skip: !id,
  });

  const {
    data: projectsData,
    loading: projectsLoading,
    refetch: refetchProjects,
  } = useAdminProjectsQuery({
      variables: {
        page: projectPage,
        pageSize: projectPageSize,
        search: projectSearch || undefined,
        user_id: id,
      },
      skip: !id,
      fetchPolicy: "cache-and-network",
    });

  const {
    data: clipsData,
    loading: clipsLoading,
    refetch: refetchClips,
  } = useAdminClipsQuery({
    variables: {
      page: clipPage,
      pageSize: clipPageSize,
      search: clipSearch || undefined,
      user_id: id,
    },
    skip: !id,
    fetchPolicy: "cache-and-network",
  });

  const [restoreExpiredItems, { loading: restoring }] =
    useAdminRestoreExpiredItemsMutation();

  const [restoreExpiredProject] = useAdminRestoreExpiredProjectMutation();
  const [restoreExpiredClip] = useAdminRestoreExpiredClipMutation();
  const [retryFailedProject] = useAdminRetryFailedProjectMutation();
  const [retryFailedClip] = useAdminRetryFailedClipMutation();

  const handleRestoreExpired = async () => {
    if (!id) return;
    try {
      const { data } = await restoreExpiredItems({
        variables: { userId: id },
      });
      const restored = data?.adminRestoreExpiredItems;
      toast({
        title: t("admin.userDetail.restoreSuccess"),
        description: t("admin.userDetail.restoreSuccessDesc", {
          projects: restored?.restoredProjects ?? 0,
          clips: restored?.restoredClips ?? 0,
        }),
      });
      refetchProjects();
      refetchClips();
    } catch {
      toast({
        title: t("admin.userDetail.restoreError"),
        description: t("admin.userDetail.restoreErrorDesc"),
        variant: "destructive",
      });
    }
  };

  const handleRestoreProject = async (projectId: string) => {
    try {
      await restoreExpiredProject({ variables: { id: projectId } });
      toast({ title: t("admin.userDetail.projectRestored") });
      refetchProjects();
    } catch {
      toast({
        title: t("admin.userDetail.restoreError"),
        variant: "destructive",
      });
    }
  };

  const handleRestoreClip = async (clipId: string) => {
    try {
      await restoreExpiredClip({ variables: { id: clipId } });
      toast({ title: t("admin.userDetail.clipRestored") });
      refetchClips();
    } catch {
      toast({
        title: t("admin.userDetail.restoreError"),
        variant: "destructive",
      });
    }
  };

  const handleRetryProject = async (projectId: string) => {
    try {
      await retryFailedProject({ variables: { id: projectId } });
      toast({ title: t("admin.userDetail.projectRetried") });
      refetchProjects();
    } catch {
      toast({
        title: t("admin.userDetail.retryError"),
        variant: "destructive",
      });
    }
  };

  const handleRetryClip = async (clipId: string) => {
    try {
      await retryFailedClip({ variables: { id: clipId } });
      toast({ title: t("admin.userDetail.clipRetried") });
      refetchClips();
    } catch {
      toast({
        title: t("admin.userDetail.retryError"),
        variant: "destructive",
      });
    }
  };

  const user = userData?.adminUser;

  const { data: walletData, refetch: refetchWallet } = useAdminUserCpmWalletQuery({
    variables: { userId: id! },
    skip: !id,
    fetchPolicy: 'cache-and-network',
  });
  const wallet = walletData?.adminUserCpmWallet;

  const [registerPayoutOpen, setRegisterPayoutOpen] = useState(false);
  const [deletePayoutId, setDeletePayoutId] = useState<string | null>(null);
  const [deletePayout] = useAdminDeleteCpmPayoutMutation();

  const handleDeletePayout = async () => {
    if (!deletePayoutId) return;
    try {
      await deletePayout({ variables: { id: deletePayoutId } });
      toast({ title: t('admin.payouts.toastDeleted') });
      setDeletePayoutId(null);
      refetchWallet();
    } catch (err) {
      toast({
        title: t('admin.payouts.toastError'),
        description: err instanceof Error ? err.message : '',
        variant: 'destructive',
      });
    }
  };

  const userPickerOption = user
    ? {
        id: user.id,
        email: user.email,
        name: user.name || null,
      }
    : null;

  const projects = projectsData?.adminProjects?.data || [];
  const projectTotalPages = projectsData?.adminProjects?.pageCount || 1;
  const projectTotal = projectsData?.adminProjects?.total || 0;
  const clips = clipsData?.adminClips?.data || [];
  const clipTotalPages = clipsData?.adminClips?.pageCount || 1;
  const clipTotal = clipsData?.adminClips?.total || 0;

  const getStatusColor = (status: string | null | undefined) => {
    switch (status) {
      case "completed":
      case "success":
        return "bg-green-500/10 text-green-500 border-green-500";
      case "processing":
        return "bg-yellow-500/10 text-yellow-500 border-yellow-500";
      case "failed":
        return "bg-red-500/10 text-red-500 border-red-500";
      case "pending":
        return "bg-blue-500/10 text-blue-500 border-blue-500";
      case "expired":
        return "bg-orange-500/10 text-orange-500 border-orange-500";
      default:
        return "bg-gray-500/10 text-gray-500 border-gray-500";
    }
  };

  const isExpiredProject = (status: string | null | undefined) =>
    status === "expired";

  const isFailedProject = (status: string | null | undefined) =>
    status === "failure";

  const isExpiredClip = (clip: AdminClip) =>
    clip.clipping_status === "expired" ||
    clip.rendering_status === "expired";

  const isFailedClip = (clip: AdminClip) =>
    clip.clipping_status === "failure" ||
    clip.rendering_status === "failure";

  if (userLoading) {
    return (
      <Layout>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </main>
      </Layout>
    );
  }

  if (!user) {
    return (
      <Layout>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
          <div className="max-w-7xl mx-auto">
            <div className="text-center py-16 text-muted-foreground">
              User not found
            </div>
          </div>
        </main>
      </Layout>
    );
  }

  return (
    <Layout>
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/admin/users">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  {t("admin.userDetail.backToUsers")}
                </Button>
              </Link>
            </div>
            <Badge variant="outline" className="text-red-500 border-red-500">
              ADMIN
            </Badge>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <User className="w-8 h-8 text-primary" />
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-bold">
                    {user.name || user.email}
                  </h1>
                  {user.is_admin && (
                    <Badge
                      variant="outline"
                      className="text-red-500 border-red-500"
                    >
                      ADMIN
                    </Badge>
                  )}
                </div>
                <p className="text-muted-foreground">{user.email}</p>
                <div className="mt-2">
                  <UserReliabilitySummary accounts={user.account_reliabilities} />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link to={`/admin/users/${id}/conversations`}>
                <Button variant="outline">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  {t("admin.botConversations.entryButton")}
                </Button>
              </Link>
              <Button
                variant="outline"
                onClick={handleRestoreExpired}
                disabled={restoring}
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                {restoring
                  ? t("admin.userDetail.restoring")
                  : t("admin.userDetail.restoreExpired")}
              </Button>
            </div>
          </div>

          {/* User Info Card */}
          <Card>
            <CardHeader>
              <CardTitle>{t("admin.userDetail.userInfo")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">
                    {t("admin.users.name")}
                  </p>
                  <p className="font-medium">{user.name || "-"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    {t("admin.users.email")}
                  </p>
                  <p className="font-medium">{user.email}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    {t("admin.users.phone")}
                  </p>
                  <p className="font-medium">{user.phone_number || "-"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    {t("admin.users.plan")}
                  </p>
                  <Badge variant="secondary">
                    {user.subscription?.plan?.name || "Free"}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    {t("admin.users.provider")}
                  </p>
                  <p className="font-medium">{user.provider || "-"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    {t("admin.users.createdAt")}
                  </p>
                  <p className="font-medium">{formatDateTime(user.createdAt)}</p>
                </div>
                {user.subscription && (
                  <>
                    <div className="md:col-span-2 lg:col-span-3">
                      <p className="text-sm text-muted-foreground mb-2">
                        {t("admin.users.usage")}
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {(() => {
                          const sub = user.subscription;
                          const timeUsed = sub.current_month_processing_seconds ?? 0;
                          const timeLimit = sub.plan?.processing_seconds ?? 0;
                          const timePercent = timeLimit > 0 ? Math.min((timeUsed / timeLimit) * 100, 100) : 0;

                          const clipsUsed = sub.current_month_clips ?? 0;
                          const clipsLimit = sub.plan?.max_clips ?? 0;
                          const clipsPercent = clipsLimit > 0 && clipsLimit < 1000000 ? Math.min((clipsUsed / clipsLimit) * 100, 100) : 0;

                          const libTimeUsed = sub.current_month_library_video_seconds ?? 0;
                          const libTimeLimit = sub.plan?.max_library_video_seconds ?? 0;
                          const libTimePercent = libTimeLimit > 0 ? Math.min((libTimeUsed / libTimeLimit) * 100, 100) : 0;

                          const libClipsUsed = sub.current_month_library_exports ?? 0;
                          const libClipsLimit = sub.plan?.max_library_exports ?? 0;
                          const libClipsPercent = libClipsLimit > 0 && libClipsLimit < 1000000 ? Math.min((libClipsUsed / libClipsLimit) * 100, 100) : 0;

                          return (
                            <>
                              <div>
                                <div className="flex items-center justify-between text-sm mb-1">
                                  <span className="flex items-center gap-1.5 text-muted-foreground">
                                    <Clock className="w-3.5 h-3.5" />
                                    {t('sidebar.time')}
                                  </span>
                                  <span className="tabular-nums text-sm">
                                    {formatHoursMinutes(timeUsed)} / {timeLimit > 0 ? formatHoursMinutes(timeLimit) : '-'}
                                    {timeLimit > 0 && <span className="ml-1 font-medium">{Math.round(timePercent)}%</span>}
                                  </span>
                                </div>
                                <Progress value={timePercent} className="h-1.5" />
                              </div>
                              <div>
                                <div className="flex items-center justify-between text-sm mb-1">
                                  <span className="flex items-center gap-1.5 text-muted-foreground">
                                    <Scissors className="w-3.5 h-3.5" />
                                    {t('sidebar.clips')}
                                  </span>
                                  <span className="tabular-nums text-sm">
                                    {clipsUsed} / {clipsLimit >= 1000000 ? '∞' : clipsLimit}
                                    {clipsLimit > 0 && clipsLimit < 1000000 && <span className="ml-1 font-medium">{Math.round(clipsPercent)}%</span>}
                                  </span>
                                </div>
                                <Progress value={clipsPercent} className="h-1.5" />
                              </div>
                              <div>
                                <div className="flex items-center justify-between text-sm mb-1">
                                  <span className="flex items-center gap-1.5 text-muted-foreground">
                                    <BookOpen className="w-3.5 h-3.5" />
                                    {t('admin.users.library')}
                                  </span>
                                  <span className="tabular-nums text-sm">
                                    {formatHoursMinutes(libTimeUsed)} / {libTimeLimit > 0 ? formatHoursMinutes(libTimeLimit) : '-'}
                                    {libTimeLimit > 0 && <span className="ml-1 font-medium">{Math.round(libTimePercent)}%</span>}
                                  </span>
                                </div>
                                <Progress value={libTimePercent} className="h-1.5" />
                              </div>
                              <div>
                                <div className="flex items-center justify-between text-sm mb-1">
                                  <span className="flex items-center gap-1.5 text-muted-foreground">
                                    <Film className="w-3.5 h-3.5" />
                                    {t('sidebar.library')} {t('sidebar.clips').toLowerCase()}
                                  </span>
                                  <span className="tabular-nums text-sm">
                                    {libClipsUsed} / {libClipsLimit >= 1000000 ? '∞' : libClipsLimit}
                                    {libClipsLimit > 0 && libClipsLimit < 1000000 && <span className="ml-1 font-medium">{Math.round(libClipsPercent)}%</span>}
                                  </span>
                                </div>
                                <Progress value={libClipsPercent} className="h-1.5" />
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {t("admin.users.payments")}
                      </p>
                      <p className="font-medium">
                        {user.subscription.payment_count ?? 0}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {t("admin.users.ltv")}
                      </p>
                      <p className="font-medium">
                        {new Intl.NumberFormat("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        }).format(user.subscription.total_payment_value ?? 0)}
                      </p>
                    </div>
                  </>
                )}
              </div>
              <div className="mt-4">
                <UserReliabilityAccounts accounts={user.account_reliabilities} />
              </div>
            </CardContent>
          </Card>

          {/* Projects Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t("admin.userDetail.projects")}</CardTitle>
                <span className="text-sm text-muted-foreground">
                  {t("admin.common.total")}: {projectTotal}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setProjectPage(1);
                }}
                className="flex gap-2 mb-4"
              >
                <Input
                  placeholder={t("admin.projects.searchPlaceholder")}
                  value={projectSearch}
                  onChange={(e) => {
                    setProjectSearch(e.target.value);
                    setProjectPage(1);
                  }}
                  className="max-w-sm"
                />
                <Button type="submit" variant="outline">
                  <Search className="w-4 h-4" />
                </Button>
              </form>

              {projectsLoading && !projectsData ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : (
                <>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t("admin.projects.name")}</TableHead>
                          <TableHead>{t("admin.projects.status")}</TableHead>
                          <TableHead>{t("admin.projects.duration")}</TableHead>
                          <TableHead>{t("admin.projects.language")}</TableHead>
                          <TableHead>{t("admin.projects.createdAt")}</TableHead>
                          <TableHead>{t("admin.users.actions")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {projects.map((project) => (
                          <TableRow key={project.id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {project.name || "-"}
                                {project.deleted && (
                                  <Badge
                                    variant="outline"
                                    className="text-red-500 border-red-500 text-xs"
                                  >
                                    Deleted
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={getStatusColor(
                                  project.curation_status
                                )}
                              >
                                {project.curation_status || "pending"}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {project.duration_seconds
                                ? formatDuration(project.duration_seconds)
                                : "-"}
                            </TableCell>
                            <TableCell>{project.language || "-"}</TableCell>
                            <TableCell>
                              {formatDateTime(project.createdAt)}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                {isExpiredProject(project.curation_status) && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                      handleRestoreProject(project.id)
                                    }
                                  >
                                    <RotateCcw className="w-3 h-3 mr-1" />
                                    {t("admin.userDetail.restore")}
                                  </Button>
                                )}
                                {isFailedProject(project.curation_status) && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                      handleRetryProject(project.id)
                                    }
                                  >
                                    <RotateCcw className="w-3 h-3 mr-1" />
                                    {t("admin.userDetail.retry")}
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                        {projects.length === 0 && (
                          <TableRow>
                            <TableCell
                              colSpan={6}
                              className="text-center py-8 text-muted-foreground"
                            >
                              {t("admin.common.noResults")}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  <div className="flex items-center justify-between mt-4">
                    <span className="text-sm text-muted-foreground">
                      {t("admin.common.page")} {projectPage}{" "}
                      {t("admin.common.of")} {projectTotalPages}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setProjectPage((p) => Math.max(1, p - 1))
                        }
                        disabled={projectPage === 1}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setProjectPage((p) =>
                            Math.min(projectTotalPages, p + 1)
                          )
                        }
                        disabled={projectPage === projectTotalPages}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Clips Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t("admin.userDetail.clips")}</CardTitle>
                <span className="text-sm text-muted-foreground">
                  {t("admin.common.total")}: {clipTotal}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setClipPage(1);
                }}
                className="flex gap-2 mb-4"
              >
                <Input
                  placeholder={t("admin.clips.searchPlaceholder")}
                  value={clipSearch}
                  onChange={(e) => {
                    setClipSearch(e.target.value);
                    setClipPage(1);
                  }}
                  className="max-w-sm"
                />
                <Button type="submit" variant="outline">
                  <Search className="w-4 h-4" />
                </Button>
              </form>

              {clipsLoading && !clipsData ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : (
                <>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t("admin.clips.name")}</TableHead>
                          <TableHead>{t("admin.clips.project")}</TableHead>
                          <TableHead>
                            {t("admin.clips.clippingStatus")}
                          </TableHead>
                          <TableHead>
                            {t("admin.clips.renderingStatus")}
                          </TableHead>
                          <TableHead>{t("admin.clips.duration")}</TableHead>
                          <TableHead>{t("admin.clips.createdAt")}</TableHead>
                          <TableHead>{t("admin.users.actions")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {clips.map((clip) => (
                          <TableRow key={clip.id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {clip.name || "-"}
                                {clip.deleted && (
                                  <Badge
                                    variant="outline"
                                    className="text-red-500 border-red-500 text-xs"
                                  >
                                    Deleted
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>{clip.project?.name || "-"}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Badge
                                  variant="outline"
                                  className={getStatusColor(clip.clipping_status)}
                                >
                                  {clip.clipping_status || "pending"}
                                </Badge>
                                {clip.clipped_video_url && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={() => {
                                      setVideoPreviewType('clipped');
                                      setVideoPreviewClip(clip);
                                    }}
                                  >
                                    <Play className="w-3 h-3" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Badge
                                  variant="outline"
                                  className={getStatusColor(
                                    clip.rendering_status
                                  )}
                                >
                                  {clip.rendering_status || "pending"}
                                </Badge>
                                {clip.finished_video_url && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={() => {
                                      setVideoPreviewType('rendered');
                                      setVideoPreviewClip(clip);
                                    }}
                                  >
                                    <Play className="w-3 h-3" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              {clip.duration_seconds
                                ? formatDuration(clip.duration_seconds)
                                : "-"}
                            </TableCell>
                            <TableCell>
                              {formatDateTime(clip.createdAt)}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                {isExpiredClip(clip) && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleRestoreClip(clip.id)}
                                  >
                                    <RotateCcw className="w-3 h-3 mr-1" />
                                    {t("admin.userDetail.restore")}
                                  </Button>
                                )}
                                {isFailedClip(clip) && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleRetryClip(clip.id)}
                                  >
                                    <RotateCcw className="w-3 h-3 mr-1" />
                                    {t("admin.userDetail.retry")}
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                        {clips.length === 0 && (
                          <TableRow>
                            <TableCell
                              colSpan={7}
                              className="text-center py-8 text-muted-foreground"
                            >
                              {t("admin.common.noResults")}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  <div className="flex items-center justify-between mt-4">
                    <span className="text-sm text-muted-foreground">
                      {t("admin.common.page")} {clipPage}{" "}
                      {t("admin.common.of")} {clipTotalPages}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setClipPage((p) => Math.max(1, p - 1))}
                        disabled={clipPage === 1}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setClipPage((p) => Math.min(clipTotalPages, p + 1))
                        }
                        disabled={clipPage === clipTotalPages}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Wallet className="w-5 h-5" /> {t('admin.payouts.userWallet.sectionTitle')}
              </CardTitle>
              <Button onClick={() => setRegisterPayoutOpen(true)}>
                {t('admin.payouts.registerButton')}
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {wallet && (
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      {t('admin.payouts.userWallet.totalEarned')}
                    </div>
                    <div className="text-xl font-bold">
                      {formatCents(wallet.total_earned_cents, wallet.currency)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      {t('admin.payouts.userWallet.totalPaidOut')}
                    </div>
                    <div className="text-xl font-bold">
                      {formatCents(wallet.total_paid_out_cents, wallet.currency)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      {t('admin.payouts.userWallet.available')}
                    </div>
                    <div className="text-xl font-bold text-emerald-400">
                      {formatCents(wallet.available_cents, wallet.currency)}
                    </div>
                  </div>
                </div>
              )}

              {wallet ? (
                wallet.payouts.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">
                    {t('admin.payouts.empty')}
                  </p>
                ) : (
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('admin.payouts.columns.amount')}</TableHead>
                          <TableHead>{t('admin.payouts.columns.currency')}</TableHead>
                          <TableHead>{t('admin.payouts.columns.paidAt')}</TableHead>
                          <TableHead>{t('admin.payouts.columns.note')}</TableHead>
                          <TableHead>{t('admin.payouts.columns.createdBy')}</TableHead>
                          <TableHead className="text-right">{t('admin.payouts.columns.actions')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {wallet.payouts.map((p) => (
                          <TableRow key={p.id}>
                            <TableCell>{formatCents(p.amount_cents, p.currency)}</TableCell>
                            <TableCell>{p.currency}</TableCell>
                            <TableCell>{formatDateTime(p.paid_at)}</TableCell>
                            <TableCell className="max-w-xs truncate" title={p.note || ''}>
                              {p.note || '—'}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {p.created_by_email || '—'}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setDeletePayoutId(p.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )
              ) : null}
            </CardContent>
          </Card>
        </div>
      </main>

      <RegisterPayoutDialog
        open={registerPayoutOpen}
        onOpenChange={setRegisterPayoutOpen}
        initialUser={userPickerOption}
        lockUser
        onCreated={() => {
          toast({ title: t('admin.payouts.toastCreated') });
          refetchWallet();
        }}
      />

      <AlertDialog
        open={!!deletePayoutId}
        onOpenChange={(o) => !o && setDeletePayoutId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.payouts.confirmDeleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.payouts.confirmDeleteBody')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('admin.payouts.cancelAction')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeletePayout}>
              {t('admin.payouts.deleteAction')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Video Preview Dialog */}
      <Dialog
        open={!!videoPreviewClip}
        onOpenChange={(open) => !open && setVideoPreviewClip(null)}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {t("admin.clips.videoPreview")} - {videoPreviewClip?.name || "-"}
            </DialogTitle>
          </DialogHeader>
          <div>
            <video
              key={videoPreviewType}
              src={
                videoPreviewClip?.clipped_video_url && videoPreviewClip?.finished_video_url
                  ? videoPreviewType === 'rendered' ? videoPreviewClip.finished_video_url : videoPreviewClip.clipped_video_url
                  : videoPreviewClip?.finished_video_url || videoPreviewClip?.clipped_video_url || ''
              }
              controls
              className="w-full rounded-md"
              style={{ maxHeight: '80vh' }}
            />
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AdminUserDetail;
